/**
 * Format: Multi-store
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API multi-store responses.
 *
 */

/**
 * Format localization.
 * - https://shopify.dev/docs/api/storefront/2023-07/objects/Localization
 * @param {Object} countries - GraphQL response.
 * @param {Array} data.instances - Shopify instances.
 * @param {Array} data.theCountries - Countries to be prefixed with 'the'.
 * @returns {Object}
 */
export function formatCountries(countries, { instances, theCountries } = {}) {
  const formattedCountries = {}

  /**
   * Find instance which has no countries assigned.
   */
  const unassignedInstance = instances.find((instance) => {
    return !instance.countries.length
  })

  /**
   * Format country response.
   */
  countries?.localization?.availableCountries?.forEach((country) => {
    formattedCountries[country.code] = {
      country: {
        code: country.code.toUpperCase(),
        currency: country.currency?.code.toUpperCase(),
        currencySymbol: country.currency?.symbol,
        name: country.name,
        the: theCountries.includes(country.code),
        freeShippingThreshold: countries.metaobjects.nodes.find((meta) => {
          return meta.code.value.toUpperCase() === country.code.toUpperCase()
        })?.freeShippingThreshold?.value * 100 || null,
      },
      instance: formatInstance(unassignedInstance),
      languages: country.availableLanguages.map((language) => {
        return {
          code: language.code.toUpperCase(),
          name: language.name,
        }
      }),
    }
  })

  /**
   * Add instance countries to list if they're not present in list.
   * - Ignores instances with no country codes set.
   */
  instances.forEach((instance) => {
    if (!instance.countries.length) {
      return
    }

    instance.countries.forEach((instanceCountry) => {
      const finalCountry = formattedCountries[instanceCountry.code] || {
        name: instanceCountry.name,
        code: instanceCountry.code,
        currency: instanceCountry.currency,
        currencySymbol: instanceCountry.currencySymbol,
        the: theCountries.includes(instanceCountry.code),
        freeShippingThreshold: countries.metaobjects.nodes.find((meta) => {
          return meta.code.value.toUpperCase() === instanceCountry.code.toUpperCase()
        })?.freeShippingThreshold?.value * 100 || null,
      }

      formattedCountries[instanceCountry.code] = {
        country: {
          ...finalCountry,
        },
        instance: formatInstance(instance),
        languages: [
          {
            code: 'EN',
            name: 'English',
          },
        ],
      }
    })
  })

  return formattedCountries
}

/**
 * Format TLD instance.
 * @param {Object} instance - Block setting instance object.
 * @returns {Object}
 */
function formatInstance(instance) {
  if (!instance) {
    return {
      url: false,
    }
  }

  /**
   * Ensure that URL ends with '/'.
   */
  const formattedMatchedUrl = instance.url.slice(-1) === '/' || instance.url.includes('?')
    ? instance.url
    : `${instance.url}/`

  /**
   * Set URL to false if current store includes URL.
   * - URL is used to update /localization submit action so set to false if
   *   we want to use the default submit action.
   */
  let url = false
  const originWithoutWww = location.origin.replace('www.', '')

  if (
    !formattedMatchedUrl.includes('localhost') &&
    !formattedMatchedUrl.includes(location.origin) &&
    !formattedMatchedUrl.includes(originWithoutWww)
  ) {
    url = formattedMatchedUrl
  }

  /**
   * Return formatted object.
   */
  return {
    url,
  }
}
